import React, { memo } from "react";
import Form from "@rjsf/core";
import type {
  RegistryFieldsType,
  RegistryWidgetsType,
  RJSFSchema,
  TemplatesType,
} from "@rjsf/utils";
import { cloneDeep } from "lodash";
import { spacing } from "../../../../../../stitches.config";
import { formatDateString } from "../../../../../../utils/dates";
import { Header } from "../../../../../Common/__styles__/FullPageFormLayout";
import { Markdown } from "../../../../../Common/__styles__/Markdown";
import { EmptyState } from "../../../../../Common/EmptyState";
import { Title } from "../../../../../Common/Typography";
import { SubmissionFormContext } from "../../../../../Submissions/Form";
import {
  DescriptionFieldTemplate,
  TitleFieldTemplate,
} from "../../../../../Submissions/Templates";
import { ColumnWrapper } from "../__styles__/SubmissionsBuilder";
import { useBuilderWatch } from "../form/form";
import {
  DocumentUploadFieldPreview,
  MultiSelectFieldPreview,
  NumberFieldPreview,
  NumberWithUnitsFieldPreview,
  PropertyMarketValueFieldPreview,
  TagFieldPreview,
  UniqueIdFieldPreview,
  UserFieldPreview,
} from "./FieldPreviews/Fields/FieldPreviews";
import { ObjectFieldTemplatePreview } from "./FieldPreviews/Templates/ObjectFieldTemplatePreview";
import {
  CheckboxWidgetPreview,
  DamageDegreeWidgetPreview,
  DateWidgetPreview,
  SelectWidgetPreview,
  TextareaWidgetPreview,
  TextWidgetPreview,
} from "./FieldPreviews/Widgets/WidgetPreviews";

export function MainColumn() {
  const [name, description, schema] = useBuilderWatch([
    "name",
    "description",
    "formStructure.schema",
  ]);

  return (
    <SubmissionFormContext.Provider
      value={{
        editing: false,
        documentUploads: null,
        defaultValues: {
          units: {
            feet: "feet",
            inches: "inches",
            hours: "hours",
            minutes: "minutes",
            days: "days",
          } as const,
          boolean: { true: true, false: false } as const,
          date: {
            today: formatDateString({
              dateString: new Date().toDateString(),
              format: "YYYY-MM-DD",
            }),
          },
          property: {
            improvementValue: 1000,
            sisd: {
              adjustmentRatio: 0.5,
            },
          },
        },
        regenerateAIFields: null,
        loadingFormPaths: {},
      }}
    >
      <ColumnWrapper role="main">
        <div style={{ maxWidth: "564px", width: "100%", margin: "0 auto" }}>
          <Header css={{ marginBottom: spacing.l }}>
            <Title size="default" type="semiBold">
              {name}
            </Title>
            {description && <Markdown markdown={description} />}
          </Header>
          {getSchemaHasAtLeastOneItem(schema) ?
            <FormWrapper />
          : <EmptyForm />}
        </div>
      </ColumnWrapper>
    </SubmissionFormContext.Provider>
  );
}

function getSchemaHasAtLeastOneItem(schema: RJSFSchema) {
  return !!schema.properties && Object.keys(schema.properties).length > 0;
}

function EmptyForm() {
  return (
    <div style={{ marginTop: "128px" }}>
      <EmptyState
        icon="add-file"
        title="No fields added to your form"
        body="Add a field to get started."
      />
    </div>
  );
}

const validatorShim = {
  isValid: () => true,
  toErrorList: () => [],
  rawValidation: () => ({}),
  validateFormData: () => ({
    errors: [],
    errorSchema: {},
  }),
};

const builderFields: RegistryFieldsType = {
  DocumentUploader: DocumentUploadFieldPreview,
  // lowercase to override the default [Select] widget preview
  multiSelect: MultiSelectFieldPreview,
  NumberField: NumberFieldPreview,
  NumberWithUnits: NumberWithUnitsFieldPreview,
  PropertyMarketValue: PropertyMarketValueFieldPreview,
  Tags: TagFieldPreview,
  UniqueId: UniqueIdFieldPreview,
  User: UserFieldPreview,
};

const builderWidgets: RegistryWidgetsType = {
  CheckboxWidget: CheckboxWidgetPreview,
  DamageDegree: DamageDegreeWidgetPreview,
  DateWidget: DateWidgetPreview,
  TextareaWidget: TextareaWidgetPreview,
  TextWidget: TextWidgetPreview,
  SelectWidget: SelectWidgetPreview,
};

const builderTemplates: Partial<TemplatesType> = {
  TitleFieldTemplate: TitleFieldTemplate,
  DescriptionFieldTemplate: DescriptionFieldTemplate,
  ObjectFieldTemplate: ObjectFieldTemplatePreview,
};

const FormWrapper = memo(function FormWrapper() {
  const formStructure = useBuilderWatch("formStructure");

  // This fixes an odd behavior where a form with no fields only updates after
  // the fourth render. This GitHub issue seems relevant and is likely the
  // cause: https://github.com/rjsf-team/react-jsonschema-form/issues/517.
  // I haven't been able to get to the bottom of why:
  // 1) it's only the UISchema that seems to matter
  // 2) it works consistently after the fourth render/change
  // 3) it's only a problem if the form had no fields on load
  const uiSchema = cloneDeep({
    ...formStructure.uiSchema,
    "ui:submitButtonOptions": { norender: true },
  });

  return (
    <Form
      schema={formStructure.schema}
      uiSchema={uiSchema}
      widgets={builderWidgets}
      fields={builderFields}
      templates={builderTemplates}
      noHtml5Validate={true}
      showErrorList={false}
      validator={validatorShim}
    />
  );
});
